import React, { Component, Suspense, lazy } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Toggler from '@fiverr/ui_toggle';
import tracker from '../../../../service/tracker';
import { EVENTS_MAPPING } from '../../../../utils/constants';
import { MENU_CONTENT_TYPES } from './consts';

const MenuContent = lazy(() => import(/* webpackChunkName: 'listings/MenuContent/MenuContent' */'./MenuContent'));
const MENU_PADDING_BOTTOM = 85;

class FloatingMenu extends Component {

    constructor(props) {
        super(props);
        this.contentRef = React.createRef();
        this.titleRef = React.createRef();
        this.applyFilters = this.applyFilters.bind(this);
        this.closeWithoutNavigation = this.closeWithoutNavigation.bind(this);
        this.clearAll = this.clearAll.bind(this);
        this.onTogglerWillOpen = this.onTogglerWillOpen.bind(this);
        this.setMaxMenuHeight = this.setMaxMenuHeight.bind(this);
        this.onMenuOpen = this.onMenuOpen.bind(this);
        this.onTogglerDidClose = this.onTogglerDidClose.bind(this);

        this.state = {
            maxMenuHeight: 200,
            navigateOnClose: true,
            renderMenuContent: false
        };
    }

    applyFilters() {
        const { activeFilters, navigationCondition, applyNavigation } = this.props;
        const { current } = this.contentRef;
        const values = current && current.getValues();

        if (navigationCondition(values, activeFilters)) {
            applyNavigation(values, activeFilters);
        } else {
            this.closeWithoutNavigation();
        }
    }

    clearAll() {
        const { activeFilters, navigationCondition, applyNavigation } = this.props;
        const values = this.contentRef.current.getValues();
        const keys = Object.keys(values).reduce((acc, key) => {
            acc[key] = undefined;
            return acc;
        }, {});

        if (navigationCondition(keys, activeFilters)) {
            applyNavigation(keys, activeFilters);
        } else {
            const { current } = this.contentRef;
            current && current.syncValues();
            this.closeWithoutNavigation();
        }
    }

    closeWithoutNavigation() {
        this.setState({ navigateOnClose: false });
        this.toggler.close();
    }

    onTogglerWillOpen() {
        this.setMaxMenuHeight();
        this.setState({ navigateOnClose: true, renderMenuContent: true });
    }

    onTogglerDidClose() {
        if (this.state.navigateOnClose) {
            this.applyFilters();
        }
    }

    setMaxMenuHeight() {
        const innerHeight = window.innerHeight;
        const { top, height } = this.titleRef.current.getBoundingClientRect();
        const maxMenuHeight = innerHeight - (top + height + MENU_PADDING_BOTTOM);

        this.setState({ maxMenuHeight });
    }

    onMenuOpen() {
        const { menuTitle, trackOpen } = this.props;
        const title = menuTitle ? `${EVENTS_MAPPING.CLICK.TARGETS.TOP_FILTER}${menuTitle}` : EVENTS_MAPPING.CLICK.TARGETS.MORE_FILTERS;
        tracker.click(title);
        trackOpen && tracker.trackFilterGroupOpen(title);
        const current = this.contentRef.current;
        current && current.onMenuOpen && current.onMenuOpen();
    }

    render() {
        const { id, data, contentType, title, titleClass, active, showApply, showClearAll, additionalProps, isGrouped } = this.props;
        const { maxMenuHeight, renderMenuContent } = this.state;

        return (
            <Toggler ref={(toggler) => this.toggler = toggler}
                togglerWillOpen={this.onTogglerWillOpen} togglerDidOpen={this.onMenuOpen} togglerDidClose={this.onTogglerDidClose}>
                {({ isOpen, toggle, preventCloseAction }) => (
                    <div className={classNames('floating-menu', { open: isOpen, grouped: isGrouped })} ref={preventCloseAction}>
                        <div className={classNames('menu-title with-carrot', titleClass, { active })}
                            onClick={toggle}
                            ref={this.titleRef}>
                            {title}
                        </div>
                        {renderMenuContent &&
                            <Suspense fallback={<div/>}>
                                <MenuContent id={id}
                                    data={data}
                                    maxMenuHeight={maxMenuHeight}
                                    showClearAll={showClearAll}
                                    showApply={showApply}
                                    contentType={contentType}
                                    additionalProps={additionalProps}
                                    applyFilters={this.applyFilters}
                                    clearAll={this.clearAll}
                                    contentRef={this.contentRef}/>
                            </Suspense>}
                    </div>
                )}
            </Toggler>
        );
    }
}

FloatingMenu.propTypes = {
    id: PropTypes.string,
    title: PropTypes.string,
    titleClass: PropTypes.string,
    menuTitle: PropTypes.string,
    active: PropTypes.bool,
    showApply: PropTypes.bool,
    showClearAll: PropTypes.bool,
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    contentType: PropTypes.oneOf(Object.keys(MENU_CONTENT_TYPES).map((key) => MENU_CONTENT_TYPES[key])),
    activeFilters: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    additionalProps: PropTypes.object
};

FloatingMenu.defaultProps = {
    id: 'more-filters',
    titleClass: 'filter-menu',
    additionalProps: {},
    showApply: true,
    showClearAll: true
};

export default FloatingMenu;
